import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Avatar, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Divider
} from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import newPointIcon from '../../../assets/images/NewPointicon.png';
import { getCookie, setCookie, eraseCookie } from '../../../infrastructure/utils/cookieStorage';
import NotificationDropdown from './NotificationDropdown';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import profileService from '../../../infrastructure/services/profileService';
import walletService from '../../../infrastructure/services/walletService';
import {
  fetchNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  getNotificationPreference
} from '../../../infrastructure/services/notificationService';

const formatNotificationTime = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateString;
  }
};

const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  if (parts.length === 1 && parts[0]) {
    return parts[0].slice(0, Math.min(parts[0].length, 2)).toUpperCase();
  }
  return '';
};

export default function PageHeader({ onMenuClick, currentUser: propCurrentUser, isLoading: propIsLoading }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);

  const [user, setUser] = useState(() => {
    const stored = getCookie('student_user');
    if (stored) {
      return {
        name: stored.name || stored.fullName || stored.displayName || 'Student',
        role: stored.role || 'Student',
        profileImage: stored.profile_image || stored.profileImage || null
      };
    }
    return { name: 'Student', role: 'Student' };
  });
  const [studentId, setStudentId] = useState(() => {
    return getCookie('studentId') || '';
  });
  const [points, setPoints] = useState(() => {
    return getCookie('userPoints') || '';
  });
  const [systemNotificationsEnabled, setSystemNotificationsEnabled] = useState(() => {
    const saved = localStorage.getItem('systemNotificationsEnabled');
    return saved === 'true';
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const fetchProfile = async () => {
    try {
      const profileData = await profileService.getProfile();
      setUser({
        name: profileData.fullName || profileData.displayName || 'Student',
        role: profileData.role || 'Student',
        profileImage: profileData.profileImage
      });
      setStudentId(profileData.ssoStudentId || 'STU-2026-0084');

      // Sync to encrypted cookies
      setCookie('studentId', profileData.ssoStudentId || 'STU-2026-0084');
      const storedUser = getCookie('student_user');
      if (storedUser) {
        setCookie('student_user', {
          ...storedUser,
          name: profileData.fullName,
          profile_image: profileData.profileImage,
          phone_number: profileData.phoneNumber
        });
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    }
  };

  const fetchWallet = async () => {
    try {
      const walletData = await walletService.getWalletBalance();
      setPoints(walletData.balance.toString());
      setCookie('userPoints', walletData.balance.toString());
    } catch (err) {
      console.error('Failed to fetch wallet balance:', err);
    }
  };

  // Sync user info on mount/active sessions
  useEffect(() => {
    fetchProfile();
    fetchWallet();

    const handleProfileUpdated = () => {
      fetchProfile();
    };

    window.addEventListener('profileUpdated', handleProfileUpdated);
    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdated);
    };
  }, []);

  // Listen for changes to localStorage (e.g., from settings page)
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('systemNotificationsEnabled');
      setSystemNotificationsEnabled(saved === 'true');
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('notificationSettingsChanged', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('notificationSettingsChanged', handleStorageChange);
    };
  }, []);

  // Sync notification preferences on load
  useEffect(() => {
    const syncPreferences = async () => {
      try {
        const pref = await getNotificationPreference();
        if (pref && typeof pref.enabled === 'boolean') {
          setSystemNotificationsEnabled(pref.enabled);
          localStorage.setItem('systemNotificationsEnabled', String(pref.enabled));
        }
      } catch (err) {
        console.error('Failed to fetch notification preference:', err);
      }
    };
    syncPreferences();
  }, []);

  useEffect(() => {
    // Load notification list & unread count
    const loadNotificationsData = async () => {
      try {
        if (systemNotificationsEnabled) {
          const list = await fetchNotifications();
          const examTypes = ['exam_started', 'EXAM_ASSIGNED', 'exam_published', 'exam_assigned'];
          const filteredList = (list || []).filter(notif => !examTypes.includes(notif.type));

          const formatted = filteredList.map(notif => ({
            id: notif.id,
            actor: notif.title || 'Notification',
            action: notif.description || '',
            target: '',
            unread: !notif.isRead,
            timestamp: formatNotificationTime(notif.createdAt),
            system: true,
            type: notif.type,
            data: notif.data
          }));
          setNotifications(formatted);

          const unreadFilteredCount = formatted.filter(n => n.unread).length;
          setUnreadCount(unreadFilteredCount);
        } else {
          setNotifications([]);
          setUnreadCount(0);
        }
      } catch (error) {
        console.error('Failed to load notifications:', error);
      }
    };

    loadNotificationsData();
    const interval = setInterval(loadNotificationsData, 30000);
    return () => clearInterval(interval);
  }, [systemNotificationsEnabled]);

  useEffect(() => {
    const handleRealtimeNotification = (event) => {
      const data = event.detail;
      if (!data) return;

      const examTypes = ['exam_started', 'EXAM_ASSIGNED', 'exam_published', 'exam_assigned'];
      if (examTypes.includes(data.type)) {
        return; // Do not show exam notifications in the general dropdown
      }

      const newNotif = {
        id: data.id || Math.random().toString(36).substring(2),
        actor: data.title || 'Notification',
        action: data.description || '',
        target: '',
        unread: true,
        timestamp: formatNotificationTime(data.createdAt || new Date()),
        system: true,
        type: data.type,
        data: data.data
      };

      setNotifications((prev) => {
        if (prev.some((n) => n.id === newNotif.id)) return prev;
        return [newNotif, ...prev];
      });
      setUnreadCount((prev) => prev + 1);
    };

    const handleRefreshNotification = async () => {
      try {
        if (systemNotificationsEnabled) {
          const list = await fetchNotifications();
          const examTypes = ['exam_started', 'EXAM_ASSIGNED', 'exam_published', 'exam_assigned'];
          const filteredList = (list || []).filter(notif => !examTypes.includes(notif.type));

          const formatted = filteredList.map(notif => ({
            id: notif.id,
            actor: notif.title || 'Notification',
            action: notif.description || '',
            target: '',
            unread: !notif.isRead,
            timestamp: formatNotificationTime(notif.createdAt),
            system: true,
            type: notif.type,
            data: notif.data
          }));
          setNotifications(formatted);

          const unreadFilteredCount = formatted.filter(n => n.unread).length;
          setUnreadCount(unreadFilteredCount);
        }
      } catch (error) {
        console.error('Failed to refresh notifications on reconnect:', error);
      }
    };

    window.addEventListener('notification:received', handleRealtimeNotification);
    window.addEventListener('notification:refresh', handleRefreshNotification);
    return () => {
      window.removeEventListener('notification:received', handleRealtimeNotification);
      window.removeEventListener('notification:refresh', handleRefreshNotification);
    };
  }, [systemNotificationsEnabled]);

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleClose();
    setIsLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    setIsLogoutDialogOpen(false);
    eraseCookie('authToken');
    eraseCookie('student_auth_token');
    eraseCookie('student_user');
    eraseCookie('studentId');
    eraseCookie('userPoints');
    navigate('/');
  };

  const handleNotificationClick = (event) => {
    setAnchorEl(null);
    if (notificationOpen) {
      setNotificationOpen(false);
      setNotificationAnchorEl(null);
    } else {
      setNotificationAnchorEl(event.currentTarget);
      setNotificationOpen(true);
    }
  };

  const handleNotificationClose = () => {
    setNotificationOpen(false);
    setNotificationAnchorEl(null);
  };

  const handleViewAllNotifications = () => {
    handleNotificationClose();
    navigate('/settings/notifications');
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, unread: false })));
      setUnreadCount(0);
      window.dispatchEvent(new Event('notificationSettingsChanged'));
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const handleNotificationItemClick = async (item) => {
    try {
      if (item.unread) {
        await markNotificationAsRead(item.id);
        setNotifications((prev) => prev.map((n) => n.id === item.id ? { ...n, unread: false } : n));
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
      if ((item.type === 'exam_started' || item.type === 'EXAM_ASSIGNED') && item.data?.classExamId) {
        handleNotificationClose();
        navigate(`/exam-manager/exam-summary/${item.data.classExamId}`);
      } else if (item.type === 'exam_published' && item.data?.examId) {
        handleNotificationClose();
        navigate(`/exam-manager`);
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const hasUnreadNotifications = systemNotificationsEnabled && (unreadCount > 0 || notifications.some((item) => item.unread));

  return (
    <Box sx={{ position: 'relative', mb: 2 }}>
      <Paper
        elevation={0}
        sx={{
          background: '#E6E9EC',
          borderRadius: '16px',
          pl: { xs: 1.25, sm: 2, md: 3 },
          pr: { xs: 1.25, sm: 2, md: 2 },
          height: '64px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '40px 1fr auto', md: '1fr auto' },
            alignItems: 'center',
            columnGap: { xs: 1, sm: 1.5, md: 2 },
            width: '100%',
            justifyContent: 'space-between'
          }}
        >
          <IconButton
            aria-label="Open sidebar"
            onClick={onMenuClick}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              border: '1px solid #e2e8f0',
              bgcolor: '#fff',
              width: 40,
              height: 40,
              justifySelf: 'start',
            }}
          >
            <MenuRoundedIcon />
          </IconButton>

          <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography sx={{ fontWeight: 600, color: '#1a202c', fontSize: { xs: 15, sm: 16 }, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Poppins' }}>
              Welcome Back, {user.name}{' '}
              <span style={{
                display: 'inline-block',
                animation: 'wave 5s ease-in-out infinite',
                transformOrigin: '70% 70%'
              }}>
                👋
              </span>
              <style>
                {`
                  @keyframes wave {
                    0%, 100% { transform: rotate(0deg); }
                    10%, 30% { transform: rotate(14deg); }
                    20%, 40% { transform: rotate(-8deg); }
                    50%, 60% { transform: rotate(14deg); }
                    70% { transform: rotate(-4deg); }
                    80% { transform: rotate(10deg); }
                  }
                  @keyframes ring {
                    0%, 100% { transform: rotate(0deg); }
                    5% { transform: rotate(15deg); }
                    10% { transform: rotate(-15deg); }
                    15% { transform: rotate(10deg); }
                    20% { transform: rotate(-10deg); }
                    25% { transform: rotate(5deg); }
                    30% { transform: rotate(-5deg); }
                    35% { transform: rotate(0deg); }
                  }
                `}
              </style>
            </Typography>
            <Typography sx={{ color: '#718096', fontSize: '12px', lineHeight: 1.2, letterSpacing: '0%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: "Poppins", fontWeight: 600, mt: 0.5 }}>
              Student ID: {studentId}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              marginLeft: 'auto',
              gap: { xs: 1, sm: 1.5, md: 2 },
            }}
          >
            {/* Points Pill */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                height: 40,
                px: 2,
                borderRadius: 9999,
                bgcolor: '#FFFDF9',
                border: '1.5px solid #FFE3C3',
                color: '#1a202c',
                cursor: 'pointer',
                transition: 'transform 0.2s ease',
                '&:hover': { transform: 'scale(1.02)' }
              }}
            >
              <Box component="img" src={newPointIcon} alt="Points" sx={{ width: 20, height: 20, display: 'block' }} />
              <Typography sx={{ fontWeight: 700, fontSize: 13.5, fontFamily: 'Poppins', color: '#1a202c' }}>
                {points}
              </Typography>
            </Box>

            {/* Notification Icon */}
            <Box
              onClick={handleNotificationClick}
              role="button"
              aria-controls={notificationOpen ? 'notification-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={notificationOpen ? 'true' : undefined}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleNotificationClick(e);
                }
              }}
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                bgcolor: '#FFFDF9',
                border: '1.5px solid #FFE3C3',
                borderRadius: '10px',
                cursor: 'pointer',
                flexShrink: 0,
                outline: 'none',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                boxShadow: notificationOpen ? '0 10px 28px rgba(249, 115, 22, 0.14)' : 'none',
                '&:hover': {
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <NotificationsNoneRoundedIcon
                sx={{
                  fontSize: 22,
                  color: '#f59e0b',
                  stroke: '#f59e0b',
                  strokeWidth: 0.35,
                  position: 'relative',
                  zIndex: 1,
                  animation: hasUnreadNotifications ? 'ring 2.5s ease-in-out infinite' : 'none',
                  transformOrigin: 'top center',
                }}
              />
              {hasUnreadNotifications && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    width: unreadCount > 9 ? 'auto' : 18,
                    minWidth: 18,
                    height: 18,
                    borderRadius: 9,
                    bgcolor: '#ef4444',
                    border: '2px solid #E6E9EC',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: unreadCount > 9 ? 0.5 : 0,
                    zIndex: 2,
                    boxSizing: 'border-box',
                    color: '#fff',
                    fontSize: '9.5px',
                    fontWeight: 700,
                    fontFamily: 'Poppins, sans-serif',
                    lineHeight: '0px',
                  }}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </Box>
              )}
            </Box>

            <NotificationDropdown
              anchorEl={notificationAnchorEl}
              open={notificationOpen}
              onClose={handleNotificationClose}
              notifications={notifications}
              onMarkAllRead={handleMarkAllNotificationsRead}
              onNotificationClick={handleNotificationItemClick}
              onViewAll={handleViewAllNotifications}
              notificationsEnabled={systemNotificationsEnabled}
            />

            {/* Profile Block (white card layout) */}
            <Box
              onClick={handleProfileClick}
              role="button"
              aria-controls={open ? 'profile-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleProfileClick(e);
                }
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.5,
                pl: 1,
                pr: 1.5,
                border: 'none',
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              <Avatar
                src={user.profileImage || undefined}
                alt="Profile Avatar"
                variant="rounded"
                sx={{
                  width: 39,
                  height: 39,
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #ff7e40 0%, #ff502d 100%)',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '14px',
                  fontFamily: 'Poppins'
                }}
              >
                {!user.profileImage && getInitials(user.name)}
              </Avatar>

              <Box sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 0, textAlign: 'left' }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 13,
                    color: '#1a202c',
                    lineHeight: 1.15,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontFamily: 'Poppins'
                  }}
                >
                  {user.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: '#718096',
                    lineHeight: 1.1,
                    fontFamily: 'Poppins',
                    mt: 0.25,
                    fontWeight: 500,
                    textTransform: 'capitalize'
                  }}
                >
                  {user.role}
                </Typography>
              </Box>

              <KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#718096' }} />
            </Box>

            {/* Dropdown Menu */}
            <Menu
              id="profile-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              onClick={handleClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              sx={{
                '& .MuiPaper-root': {
                  elevation: 0,
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 10px 30px rgba(15, 23, 42, 0.08)) drop-shadow(0px 1px 3px rgba(15, 23, 42, 0.04))',
                  mt: 1.5,
                  minWidth: 240,
                  borderRadius: '24px !important',
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  backgroundColor: 'rgba(255, 255, 255, 0.96)',
                  backdropFilter: 'blur(16px)',
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'rgba(255, 255, 255, 0.96)',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                    borderLeft: '1px solid rgba(15, 23, 42, 0.08)',
                    borderTop: '1px solid rgba(15, 23, 42, 0.08)',
                  },
                }
              }}
            >
              <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid rgba(15, 23, 42, 0.06)' }}>
                <Typography sx={{ fontWeight: 600, fontSize: 15, color: '#0f172a', fontFamily: '"Poppins", sans-serif', lineHeight: 1.3 }}>
                  {user.name}
                </Typography>
                <Box sx={{ display: 'inline-flex', mt: 0.75 }}>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#ff502d',
                      backgroundColor: 'rgba(255, 80, 45, 0.08)',
                      px: 1.5,
                      py: 0.25,
                      borderRadius: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontFamily: '"Poppins", sans-serif',
                      display: 'inline-flex',
                      alignItems: 'center'
                    }}
                  >
                    {user.role}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ p: 1 }}>
                <MenuItem
                  onClick={() => navigate('/settings')}
                  sx={{
                    py: 1.25,
                    px: 2,
                    borderRadius: '10px',
                    '&:hover': {
                      bgcolor: 'rgba(241, 245, 249, 0.8)',
                      transform: 'translateY(-1px)',
                      '& .MuiListItemIcon-root': {
                        transform: 'rotate(20deg)',
                        color: '#ff502d'
                      }
                    },
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32, transition: 'all 0.2s ease' }}>
                    <SettingsIcon sx={{ fontSize: 19, color: '#64748b' }} />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ sx: { fontSize: 13, fontWeight: 500, fontFamily: '"Poppins", sans-serif', color: '#334155' } }}>
                    Settings
                  </ListItemText>
                </MenuItem>

                <Divider sx={{ my: 0.75, borderColor: 'rgba(15, 23, 42, 0.06)' }} />

                <MenuItem
                  onClick={handleLogoutClick}
                  sx={{
                    py: 1.25,
                    px: 2,
                    borderRadius: '10px',
                    color: '#ef4444',
                    '&:hover': {
                      bgcolor: '#fef2f2',
                      transform: 'translateY(-1px)',
                      '& .MuiListItemIcon-root': {
                        transform: 'translateX(3px)'
                      }
                    },
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32, transition: 'all 0.2s ease' }}>
                    <LogoutIcon sx={{ fontSize: 19, color: '#ef4444' }} />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ sx: { fontSize: 13, fontWeight: 600, fontFamily: '"Poppins", sans-serif' } }}>
                    Logout
                  </ListItemText>
                </MenuItem>
              </Box>
            </Menu>
          </Box>
        </Box>
      </Paper>

      <ConfirmationModal
        isOpen={isLogoutDialogOpen}
        onClose={() => setIsLogoutDialogOpen(false)}
        onConfirm={handleLogoutConfirm}
        title="Confirm Logout"
        description="Are you sure you want to log out of your account? You will need to sign in again to access the portal."
        confirmText="Logout"
        cancelText="Cancel"
        type="danger"
      />
    </Box>
  );
}
